/**
 * Utility functions for getting country information from timezone or user location
 */
import { getCountryForTimezone } from 'countries-and-timezones';

/**
 * Maps country names to ISO 3166-1 alpha-2 codes
 */
const COUNTRY_NAME_TO_ISO: Record<string, string> = {
  // Common country names
  'United States': 'US',
  'United States of America': 'US',
  'USA': 'US',
  'United Kingdom': 'GB',
  'Great Britain': 'GB',
  'UK': 'GB',
  'Canada': 'CA',
  'Mexico': 'MX',
  'France': 'FR',
  'Germany': 'DE',
  'Italy': 'IT',
  'Spain': 'ES',
  'Netherlands': 'NL',
  'Belgium': 'BE',
  'Austria': 'AT',
  'Switzerland': 'CH',
  'Sweden': 'SE',
  'Denmark': 'DK',
  'Norway': 'NO',
  'Finland': 'FI',
  'Ireland': 'IE',
  'Portugal': 'PT',
  'Greece': 'GR',
  'Poland': 'PL',
  'Czech Republic': 'CZ',
  'Czechia': 'CZ',
  'Hungary': 'HU',
  'Romania': 'RO',
  'Russia': 'RU',
  'Russian Federation': 'RU',
  'Turkey': 'TR',
  'United Arab Emirates': 'AE',
  'UAE': 'AE',
  'Pakistan': 'PK',
  'India': 'IN',
  'Bangladesh': 'BD',
  'Thailand': 'TH',
  'Singapore': 'SG',
  'Hong Kong': 'HK',
  'China': 'CN',
  'Japan': 'JP',
  'South Korea': 'KR',
  'Korea': 'KR',
  'Philippines': 'PH',
  'Indonesia': 'ID',
  'Malaysia': 'MY',
  'Taiwan': 'TW',
  'Vietnam': 'VN',
  'Iran': 'IR',
  'Saudi Arabia': 'SA',
  'Kuwait': 'KW',
  'Qatar': 'QA',
  'Bahrain': 'BH',
  'Oman': 'OM',
  'Australia': 'AU',
  'New Zealand': 'NZ',
  'Egypt': 'EG',
  'South Africa': 'ZA',
  'Nigeria': 'NG',
  'Kenya': 'KE',
  'Morocco': 'MA',
  'Algeria': 'DZ',
  'Tunisia': 'TN',
  'Brazil': 'BR',
  'Argentina': 'AR',
  'Chile': 'CL',
  'Peru': 'PE',
  'Colombia': 'CO',
  'Venezuela': 'VE',
};

/**
 * Gets the country code from the user's timezone, covering every IANA timezone
 * via the `countries-and-timezones` dataset instead of a hand-maintained list.
 * @returns Country code (e.g., 'US', 'GB', 'PK') or undefined if not found
 */
export const getCountryFromTimezone = (): string | undefined => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return getCountryForTimezone(timezone)?.id;
  } catch {
    return undefined;
  }
};

/**
 * Gets the user's current timezone
 * @returns Timezone string (e.g., 'America/New_York')
 */
export const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

/**
 * Converts a country name to ISO 3166-1 alpha-2 code
 * @param countryName Country name (e.g., 'Pakistan', 'United States')
 * @returns ISO code (e.g., 'PK', 'US') or undefined if not found
 */
export const countryNameToISO = (countryName: string): string | undefined => {
  if (!countryName) return undefined;

  // Try exact match first
  if (COUNTRY_NAME_TO_ISO[countryName]) {
    return COUNTRY_NAME_TO_ISO[countryName];
  }

  // Try case-insensitive match
  const lowerCountryName = countryName.toLowerCase();
  for (const [name, code] of Object.entries(COUNTRY_NAME_TO_ISO)) {
    if (name.toLowerCase() === lowerCountryName) {
      return code;
    }
  }

  // If it's already a 2-letter code, return as-is
  if (countryName.length === 2 && /^[A-Z]{2}$/i.test(countryName)) {
    return countryName.toUpperCase();
  }

  return undefined;
};

/**
 * Gets the country code for KYC verification
 * Priority: User's saved location > Timezone-based country > Default 'US'
 * @param userCountry Optional user's saved country from profile
 * @returns ISO 3166-1 alpha-2 country code (e.g., 'US', 'GB', 'PK')
 */
export const getCountryForKYC = (userCountry?: string): string => {
  // If user has a saved country in their profile, convert it to ISO code
  if (userCountry && userCountry.length > 0) {
    const isoCode = countryNameToISO(userCountry);
    if (isoCode) {
      return isoCode;
    }
  }

  // Try to get country from timezone
  const timezoneCountry = getCountryFromTimezone();
  if (timezoneCountry) {
    return timezoneCountry;
  }

  // Default to US
  return 'US';
};
